/**
 * Festival schedule: day tabs, single horizontal stage scrub (mobile), performance placement.
 */
class ScheduleTimeline extends HTMLElement {
  connectedCallback() {
    this.#placePerformances();

    this.#tabs = Array.from(this.querySelectorAll('[data-schedule-tab]'));
    this.#panels = Array.from(this.querySelectorAll('[data-schedule-day]'));

    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', this.#onTabClick);
      tab.addEventListener('keydown', this.#onTabKeydown);
    });

    const initial =
      this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
      this.#tabs[0];

    if (initial) {
      this.#selectDay(initial.getAttribute('data-schedule-tab'), false);
    } else {
      this.#panels.forEach((panel, index) => {
        this.#setPanelActive(panel, index === 0);
      });
    }

    this.#syncAsideHeights();
    this.#bindScrub();
    this.#updateScrollbars();
  }

  disconnectedCallback() {
    this.#tabs.forEach((tab) => {
      tab.removeEventListener('click', this.#onTabClick);
      tab.removeEventListener('keydown', this.#onTabKeydown);
    });

    this.#unbindScrub();
  }

  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {AbortController | null} */
  #scrubAbort = null;

  #placePerformances() {
    const source = this.querySelector('[data-schedule-performances]');
    if (!(source instanceof HTMLElement)) return;

    const items = Array.from(source.querySelectorAll('[data-schedule-performance]'));

    items.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const day = el.getAttribute('data-day');
      const stage = el.getAttribute('data-stage');
      if (!day || !stage) return;

      const column = this.querySelector(
        `[data-schedule-day="${CSS.escape(day)}"] .schedule-timeline__column[data-stage="${CSS.escape(stage)}"]`
      );

      if (column instanceof HTMLElement) {
        column.appendChild(el);
      }
    });

    source.remove();
  }

  #onTabClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    this.#selectDay(tab.getAttribute('data-schedule-tab'), true);
  };

  #onTabKeydown = (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const currentIndex = this.#tabs.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
    if (currentIndex < 0) return;

    const nextIndex =
      event.key === 'ArrowRight'
        ? (currentIndex + 1) % this.#tabs.length
        : (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;

    const nextTab = this.#tabs[nextIndex];
    nextTab?.focus();
    this.#selectDay(nextTab?.getAttribute('data-schedule-tab'), true);
  };

  /**
   * @param {HTMLElement} panel
   * @param {boolean} active
   */
  #setPanelActive(panel, active) {
    panel.hidden = !active;
    panel.classList.toggle('is-active', active);
    panel.setAttribute('aria-hidden', active ? 'false' : 'true');
  }

  /**
   * @param {string | null} day
   * @param {boolean} focusPanel
   */
  #selectDay(day, focusPanel) {
    if (!day) return;

    this.#tabs.forEach((tab) => {
      const selected = tab.getAttribute('data-schedule-tab') === day;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    this.#panels.forEach((panel) => {
      this.#setPanelActive(panel, panel.getAttribute('data-schedule-day') === day);
    });

    if (focusPanel) {
      const active = this.#panels.find((panel) => panel.classList.contains('is-active'));
      active?.setAttribute('tabindex', '-1');
    }

    requestAnimationFrame(() => {
      this.#syncAsideHeights();
      this.#updateScrollbars();
    });
  }

  #syncAsideHeights() {
    this.querySelectorAll('[data-schedule-day].is-active').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const stages = panel.querySelector('.schedule-timeline__stages-grid');
      const stagesSpacer = panel.querySelector('.schedule-timeline__stages-spacer');
      if (stages instanceof HTMLElement && stagesSpacer instanceof HTMLElement) {
        stagesSpacer.style.minHeight = `${stages.offsetHeight}px`;
      }

      const opens = panel.querySelector('.schedule-timeline__opens-bar');
      const opensSpacer = panel.querySelector('.schedule-timeline__opens-spacer');
      if (opens instanceof HTMLElement && opensSpacer instanceof HTMLElement) {
        opensSpacer.style.minHeight = `${opens.offsetHeight}px`;
      }
    });
  }

  #unbindScrub() {
    this.#scrubAbort?.abort();
    this.#scrubAbort = null;
  }

  #updateScrollbars() {
    this.querySelectorAll('[data-schedule-day]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const scrub = panel.querySelector('[data-schedule-scrub-main]');
      const bars = Array.from(panel.querySelectorAll('[data-schedule-scrollbar]'));
      if (!(scrub instanceof HTMLElement) || bars.length === 0) return;

      const maxScroll = scrub.scrollWidth - scrub.clientWidth;
      const show = maxScroll > 4;

      bars.forEach((bar) => {
        if (!(bar instanceof HTMLElement)) return;
        bar.hidden = !show;
        if (!show) return;

        const track = bar.querySelector('.schedule-timeline__scrollbar-track');
        const thumb = bar.querySelector('[data-schedule-scrollbar-thumb]');
        if (!(track instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;

        const ratio = scrub.clientWidth / scrub.scrollWidth;
        const thumbWidth = Math.max(64, track.clientWidth * ratio);
        const maxThumbLeft = Math.max(0, track.clientWidth - thumbWidth);
        const thumbLeft = maxScroll > 0 ? (scrub.scrollLeft / maxScroll) * maxThumbLeft : 0;

        thumb.style.width = `${thumbWidth}px`;
        thumb.style.transform = `translateX(${thumbLeft}px)`;
      });
    });
  }

  /**
   * @param {HTMLElement} main
   * @param {HTMLElement} bar
   * @param {AbortSignal} signal
   */
  #bindScrollbarControl(main, bar, signal) {
    const thumb = bar.querySelector('[data-schedule-scrollbar-thumb]');
    const track = bar.querySelector('.schedule-timeline__scrollbar-track');
    if (!(thumb instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

    /** @type {number | null} */
    let thumbPointer = null;
    /** @type {number} */
    let thumbStartX = 0;
    /** @type {number} */
    let thumbStartScroll = 0;

    const scrollFromClientX = (clientX) => {
      const maxScroll = main.scrollWidth - main.clientWidth;
      if (maxScroll <= 0) return;

      const rect = track.getBoundingClientRect();
      const thumbWidth = thumb.offsetWidth;
      const maxThumbLeft = Math.max(0, track.clientWidth - thumbWidth);
      if (maxThumbLeft <= 0) return;

      const x = clientX - rect.left - thumbWidth / 2;
      const clamped = Math.min(maxThumbLeft, Math.max(0, x));
      main.scrollLeft = (clamped / maxThumbLeft) * maxScroll;
      this.#updateScrollbars();
    };

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (!(event instanceof PointerEvent)) return;
        if (event.target === thumb || thumb.contains(/** @type {Node} */ (event.target))) return;
        event.preventDefault();
        scrollFromClientX(event.clientX);
        thumbPointer = event.pointerId;
        thumbStartX = event.clientX;
        thumbStartScroll = main.scrollLeft;
        try {
          track.setPointerCapture(event.pointerId);
        } catch {
          // ignore
        }
      },
      {signal}
    );

    thumb.addEventListener(
      'pointerdown',
      (event) => {
        if (!(event instanceof PointerEvent)) return;
        event.preventDefault();
        event.stopPropagation();
        thumbPointer = event.pointerId;
        thumbStartX = event.clientX;
        thumbStartScroll = main.scrollLeft;
        try {
          thumb.setPointerCapture(event.pointerId);
        } catch {
          // ignore
        }
      },
      {signal}
    );

    const onMove = (event) => {
      if (!(event instanceof PointerEvent) || thumbPointer !== event.pointerId) return;
      event.preventDefault();

      const maxScroll = main.scrollWidth - main.clientWidth;
      if (maxScroll <= 0) return;

      const thumbWidth = thumb.offsetWidth;
      const maxThumbLeft = Math.max(0, track.clientWidth - thumbWidth);
      if (maxThumbLeft <= 0) return;

      const dx = event.clientX - thumbStartX;
      main.scrollLeft = thumbStartScroll + (dx / maxThumbLeft) * maxScroll;
      this.#updateScrollbars();
    };

    const onEnd = (event) => {
      if (!(event instanceof PointerEvent) || thumbPointer !== event.pointerId) return;
      thumbPointer = null;
    };

    thumb.addEventListener('pointermove', onMove, {passive: false, signal});
    track.addEventListener('pointermove', onMove, {passive: false, signal});
    thumb.addEventListener('pointerup', onEnd, {signal});
    track.addEventListener('pointerup', onEnd, {signal});
    thumb.addEventListener('pointercancel', onEnd, {signal});
    track.addEventListener('pointercancel', onEnd, {signal});
  }

  #bindScrub() {
    this.#unbindScrub();
    this.#scrubAbort = new AbortController();
    const {signal} = this.#scrubAbort;

    window.addEventListener('resize', () => this.#updateScrollbars(), {passive: true, signal});

    this.querySelectorAll('[data-schedule-day]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const main = panel.querySelector('[data-schedule-scrub-main]');
      if (!(main instanceof HTMLElement)) return;

      main.addEventListener('scroll', () => this.#updateScrollbars(), {passive: true, signal});

      /** @type {number | null} */
      let pointerId = null;
      /** @type {number} */
      let startX = 0;
      /** @type {number} */
      let startY = 0;
      /** @type {number} */
      let startScroll = 0;
      /** @type {boolean} */
      let axisLocked = false;
      /** @type {boolean} */
      let horizontal = false;
      /** @type {boolean} */
      let dragged = false;

      const endDrag = () => {
        pointerId = null;
        axisLocked = false;
        horizontal = false;
        main.classList.remove('is-dragging');
      };

      main.addEventListener(
        'pointerdown',
        (event) => {
          if (!(event instanceof PointerEvent)) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
          startScroll = main.scrollLeft;
          axisLocked = false;
          horizontal = false;
          dragged = false;
        },
        {signal}
      );

      main.addEventListener(
        'pointermove',
        (event) => {
          if (!(event instanceof PointerEvent) || pointerId !== event.pointerId) return;

          const dx = event.clientX - startX;
          const dy = event.clientY - startY;

          if (!axisLocked) {
            // Low threshold + slight horizontal bias so phone swipes register quickly.
            if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
            axisLocked = true;
            horizontal = Math.abs(dx) * 1.15 >= Math.abs(dy);
            if (horizontal) {
              main.classList.add('is-dragging');
              try {
                main.setPointerCapture(event.pointerId);
              } catch {
                // ignore
              }
            } else {
              // Let the page handle vertical scrolling.
              pointerId = null;
              return;
            }
          }

          if (!horizontal) return;

          event.preventDefault();
          dragged = true;
          main.scrollLeft = startScroll - dx;
          this.#updateScrollbars();
        },
        {passive: false, signal}
      );

      const finishPointer = (event) => {
        if (!(event instanceof PointerEvent) || pointerId !== event.pointerId) return;
        if (dragged) {
          const suppressClick = (clickEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
          };
          main.addEventListener('click', suppressClick, {capture: true, once: true});
          window.setTimeout(() => {
            main.removeEventListener('click', suppressClick, true);
          }, 0);
        }
        endDrag();
      };

      main.addEventListener('pointerup', finishPointer, {signal});
      main.addEventListener('pointercancel', finishPointer, {signal});

      panel.querySelectorAll('[data-schedule-scrollbar]').forEach((bar) => {
        if (bar instanceof HTMLElement) this.#bindScrollbarControl(main, bar, signal);
      });
    });
  }
}

if (!customElements.get('schedule-timeline')) {
  customElements.define('schedule-timeline', ScheduleTimeline);
}
